import { useApolloClient } from '@apollo/react-hooks';
import { isNil } from 'lodash';
import {
  getAccountDetailsQuery,
  getRenewablesQuery,
  renewablesAccountEligibilityQuery,
  updateRenewablesMutation,
} from '../components/renewable-power/renewable-power.query';
import { RenewablePowerContextState } from '../providers/RenewablePowerProvider';
import ROUTES from '../routes';
import {
  AccountDetail,
  RenewableInput,
  RenewablePower,
  RenewablePowerInput,
  RenewablesAccountEligibleRequest,
  RenewablesAccountEligibleResponse,
  HowDidYouHear,
} from '../__generated__/pge-types';
import useAccountCustomer from './useAccountCustomer';
import useAuthQuery from './useAuthQuery';
import useSelectedAccountParams from './useSelectedAccountParams';
import { useTranslation } from './useTranslation';

export interface useManageLandingPageHook {
  loading: boolean;
  content: any;
  headers: any;
  programAnswers: string[];
  eligibility: string;
  isResidential?: boolean;
  isIndustrial?: boolean;
  isSmallBusiness?: boolean;
  isLargeBusiness?: boolean;
  settings: any;
}

export default ({ ignoreNoAccounts = false }) => {
  const { richT, t } = useTranslation();
  const { encryptedAccountNumber, accountParams } = useSelectedAccountParams({
    ignoreNoAccounts,
  });
  const { customer } = useAccountCustomer();
  const apolloClient = useApolloClient();

  const programAnswers: Array<{
    key: string;
    value: string;
  }> = [
    {
      key: HowDidYouHear.Other,
      value: t('OTHER'),
    },
    {
      key: HowDidYouHear.Billinsrt,
      value: t('INSERT_IN_PGE_BILL'),
    },
    {
      key: HowDidYouHear.Event,
      value: t('EVENT'),
    },
    {
      key: HowDidYouHear.Inperson,
      value: t('OTHER_IN_PERSON_CONTACT'),
    },
    {
      key: HowDidYouHear.Letter,
      value: t('LETTER'),
    },
    {
      key: HowDidYouHear.MagazineAd,
      value: t('MAGAZINE_AD'),
    },
    {
      key: HowDidYouHear.NewspaperAd,
      value: t('NEWSPAPER_AD'),
    },
    {
      key: HowDidYouHear.RadioAd,
      value: t('RADIO_AD'),
    },
    {
      key: HowDidYouHear.TelevisionAd,
      value: t('TELEVISION_AD'),
    },
    {
      key: HowDidYouHear.NewsArticle,
      value: t('NEWS_ARTICLE'),
    },
    {
      key: HowDidYouHear.PgeWebsite,
      value: t('PGE_WEBSITE'),
    },
  ];
  const headers = [{ title: '' }];

  const content = {
    next: t('NEXT'),
    back: t('BACK'),
    cancel: t('CANCEL'),
    submit: t('SUBMIT'),
    confirm: t('CONFIRM'),
    done: t('DONE'),
    ok: t('OK'),
    title: t('RENEWABLE_PROGRAM'),
    ineligibilityPage: {
      unavailable: t('RENEWABLE_UNAVAILABLE'),
      not_eligible: t('RENEWABLE_NOT_ELIGIBLE'),
      assistance: t('ASSISTANCE'),
      phone_number: t('RESIDENTIAL_CUSTOMER_SERVICE_NUMBER'),
      between_hours: t('BETWEEN_THE_HOURS'),
      manage_renewables: t('MANAGE_RENEWABLES'),
      not_available: t('RENEWABLE_NOT_AVAILABLE'),
    },
    configPage: {
      enrollmentInfo: {
        title: t('RENEWABLE_POWER_ENROLLMENT'),
        errorNoChanges: t('RENEWABLE_CONFIG_ENROLLMENT_NO_CHANGES_ERROR'),
        errorNoOptionSelected: t(
          'RENEWABLE_CONFIG_ENROLLMENT_NO_OPTIONS_ERROR',
        ),
        soldOutReason: t('RENEWABLE_CONFIG_SOLD_OUT'),
        greenSource: {
          option: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOURCE_OPTION'),
          text: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOURCE_TEXT'),
          note: richT('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOURCE_NOTE', {
            GREEN_SOURCE_ROUTE: ROUTES.GREEN_SOURCE,
          }),
          link: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOURCE_LINK'),
          url: ROUTES.GREEN_SOURCE_PDF,
        },
        cleanWind: {
          option: t('RENEWABLE_CONFIG_ENROLLMENT_CLEAN_WIND_OPTION'),
          text: t('RENEWABLE_CONFIG_ENROLLMENT_CLEAN_WIND_TEXT'),
          note: richT('RENEWABLE_CONFIG_ENROLLMENT_CLEAN_WIND_NOTE', {
            CLEAN_WIND_ROUTE: ROUTES.CLEAN_WIND_URL,
          }),
          link: t('RENEWABLE_CONFIG_ENROLLMENT_CLEAN_WIND_LINK'),
          url: ROUTES.GREEN_SOURCE_PDF,
          blocks: t('BLOCKS'),
        },
        greenSolar: {
          option: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_OPTION'),
          text: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_TEXT'),
          textEnrolled: t(
            'RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_ENROLLED_TEXT',
          ),
          note: richT('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_NOTE', {
            GREEN_FUTURE_SOLAR_ROUTE: ROUTES.GREEN_FUTURE_SOLAR,
          }),
          noteUrl: ROUTES.GREEN_FUTURE_SOLAR,
          disableNote: t(
            'RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_DISABLED_NOTE',
          ),
          callNote: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_CALL_NOTE'),
          callNoteEnrolled: t(
            'RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_CALL_ENROLLED_NOTE',
          ),
          notificationNote: richT(
            'RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_NOTIFICATION_NOTE',
            {
              ENROLLMENT_NOTIFICATION_ROUTE:
                ROUTES.RENEWABLE_ENROLLMENT_NOTIFICATION,
            },
          ),
          notificationUrl: ROUTES.GREEN_FUTURE_SOLAR_RESIDENTIAL_ENROLL,
          link: t('RENEWABLE_CONFIG_ENROLLMENT_GREEN_SOLAR_LINK'),
          url: ROUTES.GREEN_FUTURE_SOLAR_PDF,
        },
      },
      habitatInfo: {
        title: t('RENEWABLE_CONFIG_HABITAT_SUPPORT_TITLE'),
        text: richT('RENEWABLE_CONFIG_HABITAT_SUPPORT_TEXT', {
          HABITAT_SUPPORT_ROUTE: ROUTES.HABITAT_SUPPORT,
          HABITAT_PRICE: t('RENEWABLE_HABITAT_PRICE'),
        }),
        option: t('RENEWABLE_CONFIG_HABITAT_SUPPORT_OPTION'),
      },
      programInfo: {
        title: t('RENEWABLE_POWER_PROGRAM_AWARENESS'),
        question: t('RENEWABLE_CONFIG_PROGRAM_QUESTION'),
        noAnswer: t('RENEWABLE_POWER_PROGRAM_SELECT_ONE'),
        promo: t('RENEWABLE_POWER_PROGRAM_PROMO_CODE'),
        promoLabel: t('RENEWABLE_POWER_PROGRAM_PROMO_CODE_LABEL'),
        promoCodeError: t('RENEWABLE_POWER_PROGRAM_PROMO_CODE_ERROR'),
      },
      companyInfo: {
        title: t('RENEWABLE_POWER_CONFIG_COMPANY_TITLE'),
        text: t('RENEWABLE_POWER_CONFIG_COMPANY_TEXT'),
        yesOption: t('RENEWABLE_POWER_CONFIG_COMPANY_OPTION1'),
        noOption: t('RENEWABLE_POWER_CONFIG_COMPANY_OPTION2'),
        question: t('RENEWABLE_POWER_CONFIG_COMPANY_QUESTION'),
        companyName: t('COMPANY_NAME'),
        errorNoCompanyName: t('RENEWABLE_CONFIG_COMPANY_NO_COMPANY_ERROR'),
        errorInvalidCompanyName: t(
          'RENEWABLE_CONFIG_COMPANY_INVALID_COMPANY_ERROR',
        ),
      },
    },
    confirmPage: {
      subtitle: t('RENEWABLE_POWER_SUMMARY_SUBTITLE'),
      note: t('RENEWABLE_POWER_SUMMARY_NOTE'),
      addedOptionsLabel: t('RENEWABLE_POWER_SUMMARY_ADDED'),
      cancelledOptionsLabel: t('RENEWABLE_POWER_SUMMARY_CANCELLED'),
      keptOptionsLabel: t('RENEWABLE_POWER_SUMMARY_KEPT'),
      greenSource: t('RENEWABLE_GREEN_SOURCE'),
      cleanWind: t('RENEWABLE_CLEAN_WIND'),
      greenSolar: t('RENEWABLE_GREEN_SOLAR'),
      habitat: t('RENEWABLE_HABITAT'),
    },
    donePage: {
      title: t('RENEWABLE_POWER_DONE_TITLE'),
      text: t('RENEWABLE_POWER_DONE_TEXT'),
    },
    industrialForm: {
      industrial_renewable: t('INDUSTRIAL_RENEWABLE'),
      contact_info: t('CONTACT_INFORMATION'),
      contact_name: t('CONTACT_NAME'),
      email: t('EMAIL_ADDRESS'),
      phone: t('PHONE_NUMBER'),
      industrial_renewable_2: t('INDUSTRIAL_RENEWABLE_2'),
      nameError: t('CONTACT_NAME_INVALID'),
      emailErrorInvalid: t('ERROR_AUTH_BAD_EMAIL'),
      emailErrorBlank: t('BLANK_EMAIL'),
      phoneError: t('ERROR_PHONE_REQUIRED'),
      notification: t('INDUSTRIAL_RENEWABLE_NOTIFICATION'),
    },
  };

  const settings = {
    habitatPrice: t('RENEWABLE_HABITAT_PRICE'),
  };

  const { loading: accountLoading, data: accountData, refetch } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetailsQuery, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
  });

  const accountDetails = accountData?.getAccountDetails?.[0];

  const encryptedPersonId = accountDetails?.isLoggedInUserOnAccount
    ? customer?.encryptedPersonId
    : accountParams?.encryptedPersonId ?? undefined;

  const { data: eligibilityData, loading: eligibilityLoading } = useAuthQuery<
    { renewablesAccountEligibility: RenewablesAccountEligibleResponse },
    { payload: RenewablesAccountEligibleRequest }
  >(renewablesAccountEligibilityQuery, {
    variables: {
      payload: {
        encryptedAccountNumber,
      },
    },
    fetchPolicy: 'network-only',
    skip:
      accountData === undefined ||
      accountLoading ||
      isNil(encryptedAccountNumber) ||
      isNil(encryptedPersonId),
  });

  const { data: renewableData, loading: renewableDataLoading } = useAuthQuery<
    { getRenewables: RenewablePower },
    { payload: RenewableInput }
  >(getRenewablesQuery, {
    variables: {
      payload: {
        encryptedServiceAgreementId:
          eligibilityData?.renewablesAccountEligibility
            ?.encryptedServiceAgreementId || '',
      },
    },
    fetchPolicy: 'network-only',
    skip: isNil(
      eligibilityData?.renewablesAccountEligibility
        ?.encryptedServiceAgreementId,
    ),
  });

  function updateRenewableEnrollment(
    state: RenewablePowerContextState,
    eligibilityInfo: RenewablesAccountEligibleResponse | null,
  ) {
    const payload: RenewablePowerInput = {
      encryptedSaId: eligibilityInfo?.encryptedServiceAgreementId,
      howDidYouHear:
        state.selectedProgramAnswer && state.selectedProgramAnswer !== 'Select'
          ? (state.selectedProgramAnswer as HowDidYouHear)
          : null,
      promoCode: state?.promoCode,
      promotionalOpportunity: {
        isIncludeInPromotion: state?.includeInPromotion,
        companyName: state?.companyName,
      },
      isGreenSourceSelected: state.greenSource.isSelected,
      isCleanWindSelected: state.cleanWind.isSelected,
      cleanWindBlocks: state.cleanWind.blocks,
      isGreenFutureSolarSelected: state.solarSource.isSelected,
      isHabitatSupportSelected: state.habitatSupport.isSelected,
      greenFutureSolarBlocks: state.solarSource.blocks,
      cssUser: customer?.email,
    };

    if (!state.includeInPromotion) {
      delete payload.promotionalOpportunity?.companyName;
    }

    return apolloClient.mutate<{
      updateRenewables: boolean;
    }>({
      mutation: updateRenewablesMutation,
      variables: { payload },
      errorPolicy: 'all',
    });
  }

  return {
    loading: accountLoading || eligibilityLoading || renewableDataLoading,
    content,
    headers,
    programAnswers,
    settings,
    eligibilityData,
    accountDetails,
    renewableData,
    updateRenewableEnrollment,
    customer,
    encryptedPersonId,
  };
};
